import React, { useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { ThemeProvider } from "@/contexts/ThemeContext";

// Pages
import KioskHome from "@/pages/kiosk/KioskHome";
import LoginPage from "@/pages/admin/LoginPage";
import DashboardPage from "@/pages/admin/DashboardPage";
import ReportsPage from "@/pages/admin/ReportsPage";
import EmployeesPage from "@/pages/admin/EmployeesPage";
import SitesZonesPage from "@/pages/admin/SitesZonesPage";
import ShiftsPage from "@/pages/admin/ShiftsPage";
import SchedulingPage from "@/pages/admin/SchedulingPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import UsersPage from "@/pages/admin/UsersPage";
import PlatformAdminPage from "@/pages/admin/PlatformAdminPage";
import LookupsPage from "@/pages/admin/LookupsPage";
import PayrollPage from "@/pages/admin/PayrollPage";
import ProfilePage from "@/pages/admin/ProfilePage";
import AnalyticsPage from "@/pages/admin/AnalyticsPage";
import DocumentationPage from "@/pages/admin/DocumentationPage";
import PaieRHPage from "@/pages/admin/PaieRHPage";

// Layouts
import DashboardLayout from "@/layouts/DashboardLayout";

// Subdomain detection utility
// Plan B: Single domain (pointage.tmbsaas.com) serves both kiosk and admin
// - Root "/" = Kiosk (for factory workers)
// - "/admin/*" = Admin interface (for managers)
// - "/kiosk" = Also kiosk (backward compatibility)
const getAppMode = () => {
  const hostname = window.location.hostname;
  
  // Preview environments - full mode (admin + kiosk accessible)
  if (hostname.includes('.preview.emergentagent.com')) {
    return 'full';
  }
  
  // KIOSK-ONLY mode: only for dedicated kiosk subdomains (punch.*, kiosk.*)
  // NOT for pointage.tmbsaas.com which serves both interfaces
  if (hostname.startsWith('punch.') || hostname.startsWith('kiosk.')) {
    return 'kiosk';
  }
  
  // pointage.tmbsaas.com and all other domains: full mode
  // Root "/" = kiosk, "/admin/*" = admin
  return 'full';
};

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
}

// Kiosk-only Routes (for punch.tmconsultingf.com)
function KioskRoutes() {
  return (
    <Routes>
      {/* All routes redirect to kiosk */}
      <Route path="/" element={<KioskHome />} />
      <Route path="/kiosk" element={<KioskHome />} />
      
      {/* Block admin access - redirect to kiosk */}
      <Route path="/admin/*" element={<KioskAccessDenied />} />
      
      {/* Fallback - always go to kiosk */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Access Denied component for kiosk subdomain trying to access admin
function KioskAccessDenied() {
  const hostname = window.location.hostname;
  // Build the admin URL from the current domain
  const getAdminUrl = () => {
    if (hostname.startsWith('pointage.')) {
      return `https://pointageadmin.${hostname.split('.').slice(1).join('.')}`;
    }
    if (hostname.startsWith('punch.') || hostname.startsWith('kiosk.')) {
      return `https://admin.${hostname.split('.').slice(1).join('.')}`;
    }
    return '/admin/login';
  };
  
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-2xl p-8 max-w-md text-center border border-slate-700">
        <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Mode Kiosque</h1>
        <p className="text-slate-400 mb-6">
          Ce terminal est configuré en mode kiosque de pointage.
        </p>
        <div className="flex flex-col gap-3">
          <a 
            href="/"
            className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
            data-testid="back-to-kiosk-btn"
          >
            Retour au pointage
          </a>
          <a 
            href={getAdminUrl()}
            className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="go-to-admin-link"
          >
            Accéder à l'administration
          </a>
        </div>
      </div>
    </div>
  );
}

// Full Routes - Single domain serving both kiosk (root) and admin (/admin/*)
// pointage.tmbsaas.com = kiosk at root, admin at /admin
function FullRoutes() {
  return (
    <Routes>
      {/* Root = Kiosk (factory workers just type pointage.tmbsaas.com) */}
      <Route path="/" element={<KioskHome />} />
      <Route path="/kiosk" element={<KioskHome />} />
      
      {/* Admin Login */}
      <Route path="/login" element={<Navigate to="/admin/login" replace />} />
      <Route path="/admin/login" element={<LoginPage />} />
      
      {/* Protected Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="sites-zones" element={<SitesZonesPage />} />
        <Route path="shifts" element={<ShiftsPage />} />
        <Route path="scheduling" element={<SchedulingPage />} />
        <Route path="lookups" element={<LookupsPage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="platform" element={<PlatformAdminPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="paierh" element={<PaieRHPage />} />
        <Route path="documentation" element={<DocumentationPage />} />
      </Route>
      
      {/* Fallback - go to kiosk */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Admin/Full Routes (for admin.* subdomains - legacy support)
function AdminRoutes() {
  return (
    <Routes>
      {/* Root redirects to admin login */}
      <Route path="/" element={<Navigate to="/admin/login" replace />} />
      <Route path="/kiosk" element={<KioskHome />} />
      
      {/* Admin Login */}
      <Route path="/admin/login" element={<LoginPage />} />
      
      {/* Protected Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="sites-zones" element={<SitesZonesPage />} />
        <Route path="shifts" element={<ShiftsPage />} />
        <Route path="scheduling" element={<SchedulingPage />} />
        <Route path="lookups" element={<LookupsPage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="platform" element={<PlatformAdminPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="paierh" element={<PaieRHPage />} />
        <Route path="documentation" element={<DocumentationPage />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/admin/login" replace />} />
    </Routes>
  );
}

// Main App Routes - determines which routes to use based on subdomain
function AppRoutes() {
  const appMode = useMemo(() => getAppMode(), []);
  
  console.log(`[PunchTM] Running in ${appMode} mode on ${window.location.hostname}`);
  
  if (appMode === 'kiosk') {
    return <KioskRoutes />;
  }
  
  if (appMode === 'full') {
    return <FullRoutes />;
  }
  
  return <AdminRoutes />;
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <TenantProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </TenantProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
